/// <amd-module name="Build/Pipelines/Tabs/Runs/RunsTable" />
import { BuildSignalRActionHub } from "Actions/SignalR";
import { Build, BuildDefinition, BuildReason, BuildResult, BuildStatus, DefinitionReference, TaskResult, TimelineRecordState } from "Build/Client/Contracts/Build";
import { FlatGraphRecord, RepositoryResourceData, RetentionLeaseData, RunsListItemData } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { IConfirmPipelineActionProps } from "Build/Common/Commands/ConfirmPipelineAction/Command.types";
import { IQueueBuildProps } from "Build/Common/Commands/QueueBuild/Command.types";
import { BranchLink } from "Build/Common/Library/Components/BranchLink";
import { CommandComponent } from "Build/Common/Library/Components/CommandComponent";
import { CommitLink } from "Build/Common/Library/Components/CommitLink";
import { RepoLink } from "Build/Common/Library/Components/RepoLink";
import { RetentionPanel, RetentionPanelProps } from "Build/Common/Library/Components/RetentionPanel";
import { TelemetryConstants } from "Build/Common/Library/Constants";
import { handleError } from "Build/Common/Library/ErrorHandler";
import { getBuildStatusIndicatorData } from "Build/Common/Library/Indicator";
import { formatPipelineRunName } from "Build/Common/Library/PipelineRunNameFormatter";
import { Security } from "Build/Common/Library/Security";
import { ContributionDataActionHub } from "Build/Flux/Actions/ContributionData";
import { DateAndDuration } from "Build/Pipelines/Components/DateAndDuration";
import { StageGraph, StageGraphNode } from "Build/Pipelines/Components/StageGraph";
import { MaxUpdates } from "Build/Pipelines/Constants";
import { Performance } from "Build/Pipelines/Performance";
import * as Resources from "Build/Pipelines/Resources";
import { RunsTableStages, getStageNode } from "Build/Pipelines/Tabs/Runs/RunsTableStages";
import { IRunsProviderData, RunsStore } from "Build/Pipelines/Tabs/Runs/Stores/RunsStore";
import * as BuildReasonTools from "Build/Pipelines/Tools/BuildReasonTools";
import { GlobalMessageLevel, IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import { ScreenBreakpoints, ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { onClickFPS } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssIdentityService } from "VSS/Platform/Identity";
import { IVssComponentContext, IVssComponentProperties, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Card } from "VSSUI/Card";
import { TitleSize } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Status as StatusComponent, StatusSize } from "VSSUI/Status";
import {
    ColumnMore,
    ITableBreakpoint, ITableColumn, ITableRow,
    ITableRowDetails,
    SimpleTableCell,
    Table,
    TableCell,
    TableColumnStyle, TableRow,
    TwoLineTableCell,
    renderLoadingCell
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { VssPersona } from "VSSUI/VssPersona";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import * as React from "react";
import { INewBranchFilter } from "./RunsFilterBar.types";
import { RunsAppliedFilterStore } from "./Stores/RunsAppliedFilterStore";


export interface IRunsTableProps extends IVssComponentProperties {
    isPageLoad: boolean;
    appliedFilterStore: RunsAppliedFilterStore;
    signalRActionHub: BuildSignalRActionHub;
    security: Security;
    userId?: string;
    canQueueBuild?: boolean;
    definitionId?: number;
    definitionScope?: string;
    getBranchesOnNewRun?: (branches: INewBranchFilter[]) => void;
}

export interface IRunsTableState {
    multiRepo: boolean;
}

interface IRunRow extends RunsListItemData {
    isNewRunsRow?: boolean;
    isLoadItem?: boolean;
    renderRow?: (index: number, item: IRunRow, details: ITableRowDetails<IRunRow>) => JSX.Element;
}

export class RunsTable extends VssComponent<IRunsTableProps, IRunsTableState> {
    private _store: RunsStore;
    private _identityService: IVssIdentityService;
    private _telemetryService: IVssTelemetryService;
    private _itemProvider: IObservableArray<IRunRow | ObservableValue<IRunRow | undefined>>;
    private _ttiMarked = false;
    private _columns: ITableColumn<IRunRow>[];

    private _tableBreakpoints: ITableBreakpoint[] = [
        {
            breakpoint: ScreenBreakpoints.xsmall,
            columnWidths: [30, -300, 0, 0, 0]
        },
        {
            breakpoint: ScreenBreakpoints.small,
            columnWidths: [0, 0, -70, -30, 156]
        },
        {
            breakpoint: ScreenBreakpoints.medium,
            columnWidths: [0, 0, -60, -50, 156]
        },
        {
            breakpoint: ScreenBreakpoints.large,
            columnWidths: [0, 0, -50, -50, 156]
        }
    ];

    constructor(props: IRunsTableProps, context: IVssComponentContext) {
        super(props, context);

        this._store = new RunsStore({
            pageContext: context.pageContext,
            contributionActionHub: new ContributionDataActionHub<IRunsProviderData>(),
            appliedFilterStore: props.appliedFilterStore,
            definitionId: props.definitionId,
            definitionScope: props.definitionScope,
            signalRActionHub: props.signalRActionHub,
            getBranchesOnNewRun: props.getBranchesOnNewRun
        });

        
        this._store.fetchData(!props.isPageLoad);
        const runs: IRunRow[] = this._store.getRunData().slice();

        this._itemProvider = new ObservableArray<IRunRow>(runs);

        if (this._store.hasMore()) {
            const asyncRow = new ObservableValue<IRunRow | undefined>(undefined);
            this._itemProvider.push(asyncRow);
        }
        this._identityService = this.context.pageContext.getService<IVssIdentityService>("IVssIdentityService");        
        this._telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");

        this._setColumns();

        const multiRepo = this._isMultiRepo(runs);
        this.state = {
            multiRepo
        };
    }

    public render() {
        if (this._itemProvider.length === 0) {
            if (this._store.isLoading()) {
                return (
                    <Card className="flex-grow bolt-card-no-vertical-padding pipelines-minimum-card-height" contentProps={{ contentPadding: false }}>
                        <Spinner className="pipelines-filter-spinner" size={SpinnerSize.large} label={Resources.Loading} />
                    </Card>
                );
            }

            if (this.props.appliedFilterStore.hasAppliedFilters() || this.props.definitionScope) {
                const image = getAssetLocation(this.context.pageContext, "ms.vss-build-web/pipelines/Content/no-results.png");
                const text = this.props.definitionScope ? Resources.NoRunsFound : Resources.NoResultsFound;
                Utils_Accessibility.announce(text);
                return (
                    <Card className="flex-grow bolt-card-no-vertical-padding pipelines-minimum-card-height" contentProps={{ contentPadding: false }}>
                        <div className="pipelines-zero-data pipelines-whole-page-centered">
                            <ZeroData imageAltText={text} imagePath={image} secondaryText={text} />
                        </div>
                    </Card>
                );
            }

            if (!!this.props.definitionId && this.props.canQueueBuild) {
                const image = getAssetLocation(this.context.pageContext, "ms.vss-build-web/pipelines/Content/no-runs.png");
                Utils_Accessibility.announce(Resources.NoRunsGetStarted);
                return (
                    <div className="pipelines-zero-data pipelines-whole-page-centered">
                        <ZeroData
                            imagePath={image}
                            imageAltText={Resources.NoRunsGetStarted}
                            secondaryText={Resources.NoRunsGetStarted}
                            actionText={Resources.RunPipeline}
                            actionType={ZeroDataActionType.ctaButton}
                            onActionClick={this._onZeroDataButtonClick}
                        />
                    </div>
                );
            } else {
                const image = getAssetLocation(this.context.pageContext, "ms.vss-build-web/pipelines/Content/no-results.png");
                Utils_Accessibility.announce(Resources.NoRunsFound);
                return (
                    <Card className="flex-grow bolt-card-no-vertical-padding pipelines-minimum-card-height" contentProps={{ contentPadding: false }}>
                        <div className="pipelines-zero-data pipelines-whole-page-centered">
                            <ZeroData imagePath={image} imageAltText={Resources.NoRunsFound} secondaryText={Resources.NoRunsFound} />
                        </div>
                    </Card>
                );
            }
        }
        let cardTitle: string | undefined = undefined;
        if (!this.props.definitionId && !this.props.definitionScope) {
            if (this.props.appliedFilterStore.hasAppliedFilters()) {
                cardTitle = Resources.FilteredRunsView;
            } else {
                cardTitle = Resources.RunsView;
            }
        }
        if (this.props.definitionScope) {
            if (this.props.appliedFilterStore.hasAppliedFilters()) {
                cardTitle = Resources.FilteredRunsView;
            } else {
                cardTitle = format(Resources.RunsViewForFolder, this.props.definitionScope);
            }
        }
        return (
            <div className="flex-column flex-grow">
                {this.props.definitionId && (
                    <WrappedComponent
                        dependencies={["ms.vss-build-web.pipeline-insights.runs-insights"]}
                        wrappedType="pipelines-runs-insights"
                        definitionId={this.props.definitionId}
                    />
                )}
                <Card
                    className="flex-grow bolt-table-card"
                    contentProps={{ contentPadding: false }}
                    titleProps={cardTitle ? { text: cardTitle, size: TitleSize.Medium } : undefined}
                >
                    <Table
                        ariaLabel={Resources.RunsTabName}
                        className="runs-table"
                        columns={this._getTableColumns()}
                        itemProvider={this._itemProvider}
                        onActivate={this._onActivateRow}
                        renderLoadingRow={this._renderLoadRow}
                        showHeader={screenSize => screenSize >= ScreenSize.small}
                        tableBreakpoints={this._getTableBreakpoints()}
                    />
                </Card>
            </div>
        );
    }

    public componentDidMount() {
        this._store.addListener(this._onStoreChanged);
        this._markTTI();
        this._sendTelemetryEvent();
    }

    public componentWillUnmount() {
        super.componentWillUnmount();
        this._store.dispose();
    }

    public componentDidUpdate() {
        this._markTTI();
    }

    private _sendTelemetryEvent() {   
        const tfsPageData = getTFSPageData(this.context.pageContext); 
        const projectId = tfsPageData?.project?.id;
        this._telemetryService.publishEvent(TelemetryConstants.Area, TelemetryConstants.RunsOverviewShow, {
            ProjectId: projectId
        });
    }

    private _markTTI() {
        if (!this._ttiMarked && !this._store.isLoading()) {
            this._ttiMarked = true;
            new Performance(this.context.pageContext).mark("runs", this.props.definitionId);
        }
    }

    private _getTableBreakpoints = (): ITableBreakpoint[] => {
        return this._tableBreakpoints;
    };

    private _getTableColumns = () => {
        return this._columns;
    };

    private _renderDescriptionNonMobileCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RunsListItemData>,
        tableItem: RunsListItemData
    ): JSX.Element => {
        return this._renderDescriptionCell(rowIndex, columnIndex, tableColumn, tableItem);
    };

    private _renderDescriptionMobileCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RunsListItemData>,
        tableItem: RunsListItemData
    ): JSX.Element => {
        // For mobile, don't make links clickable since we want the whole row become a tap target
        return this._renderDescriptionCell(rowIndex, columnIndex, tableColumn, tableItem, false);
    };

    private _renderDescriptionCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RunsListItemData>,
        tableItem: RunsListItemData,
        linksClickable: boolean = true
    ): JSX.Element => {
        const build = tableItem.run;
        if (build) {
            let triggeringRepository: RepositoryResourceData;
            if (build.triggeringRepository) {
                triggeringRepository = build.triggeringRepository;
            } else {
                triggeringRepository = {
                    id: build.repository.id,
                    name: build.repository.name,
                    alias: "self",
                    connectionId: build.repository.url,
                    refName: build.sourceBranch,
                    version: build.sourceVersion,
                    type: build.repository.type,
                    projectId: "",
                    projectName: "",
                    url: ""
                };
            }

            let sourceBranch = triggeringRepository.refName;
            let sourceVersion = triggeringRepository.version;
            
            if (isFeatureFlagEnabled(this.context.pageContext, "Build2.SelectTriggeringRepositoriesBasedOnSHA") && build.reason == BuildReason.PullRequest) {
                // this scenario can happen if the repository is not using the triggering branch as resource. This can only happen 
                // when a PR trigger is set up on a different repository.
                sourceBranch = build.sourceBranch;
                sourceVersion = build.sourceVersion;
            }

            const reasonIcon = <Icon iconName={BuildReasonTools.getReasonIcon(build.reason)} className="icon-margin" />;
            const numberString = build.buildNumber ? "#" + build.buildNumber + " " : "";
            const runName = formatPipelineRunName(build.buildNumber, tableItem.sourceVersionMessage, build.appendCommitMessageToRunName);
            const definitionString = build.definition && build.definition.name ? format(Resources.OnDefinition, build.definition.name) : "";
            const projectNamePromise = this._store.getProjectNamePromise(triggeringRepository.id);

            const repoLink = this.state.multiRepo && (
                <RepoLink
                    sourceRepositoryId={triggeringRepository.id}
                    sourceRepositoryName={triggeringRepository.name}
                    sourceRepositoryType={triggeringRepository.type}
                    sourceRepositoryUrl={triggeringRepository.connectionId}
                />
            );
            const branchLink = (
                <BranchLink
                    repositoryType={triggeringRepository.type}
                    repositoryName={triggeringRepository.name}
                    repositoryId={triggeringRepository.id}
                    repositoryUrl={triggeringRepository.connectionId}
                    branch={sourceBranch}
                    monospace={true}
                    readonly={!linksClickable}
                    projectNamePromise={projectNamePromise}
                />
            );
            const sourceVersionLink = (
                <CommitLink
                    repositoryName={triggeringRepository.name}
                    repositoryId={triggeringRepository.id}
                    repositoryType={triggeringRepository.type}
                    commitId={sourceVersion}
                    monospace={true}
                    readonly={!linksClickable}
                    projectNamePromise={projectNamePromise}
                />
            );

            const retainedIndicator =
                build.retentionLeases && build.retentionLeases.length > 0 ? (
                    <Icon iconName="Pinned" ariaLabel={Resources.Retained} tooltipProps={{ text: Resources.Retained }} />
                ) : undefined;

            let userImage;
            let imageUrl;
            if (build.requestedFor) {
                imageUrl = this._identityService.getIdentityImageUrl(build.requestedFor);
                userImage = (
                    <span className="padding-left-4">
                        <VssPersona className="v-align-middle" imageUrl={imageUrl} size="extra-small" />
                    </span>
                );
            }

            const reasonDescription = (
                <span className="text-ellipsis flex-row">
                    {BuildReasonTools.getReasonText(build.reason, !!imageUrl)}
                    {userImage}
                </span>
            );

            let topLine: JSX.Element;
            let bottomLine: JSX.Element;
            if (!!this.props.definitionId) {
                topLine = (
                    <span className="fontWeightSemiBold text-ellipsis bolt-table-inline-link-left-padding">
                        {runName}
                    </span>
                );
                bottomLine = (
                    <span className="fontSizeMS secondary-text flex-row flex-baseline text-ellipsis bolt-table-inline-link-left-padding pipeline-firefox-fix">
                        {reasonIcon}
                        {reasonDescription}
                        {repoLink}
                        {branchLink}
                        {sourceVersionLink}
                        {retainedIndicator}
                    </span>
                );
            } else {
                let runLabel = tableItem.sourceVersionMessage;
                if (!runLabel || runLabel.length === 0) {
                    runLabel = format(Resources.RunLabel, numberString);
                }
                topLine = <span className="fontWeightSemiBold text-ellipsis bolt-table-inline-link-left-padding">{runLabel}</span>;
                bottomLine = (
                    <span className="fontSizeMS secondary-text flex-row flex-baseline text-ellipsis bolt-table-inline-link-left-padding pipeline-firefox-fix">
                        {reasonIcon}
                        {numberString}
                        {definitionString}
                        {repoLink}
                        {branchLink}
                        {sourceVersionLink}
                        {retainedIndicator}
                    </span>
                );
            }

            let iconProps;
            if(isFeatureFlagEnabled(this.context.pageContext, "Build2.RunsView.EnableOverviewStatus")) {
                iconProps = {
                    render: () => this._renderBuildStatus(tableItem)
                };
            }

            return (
                <TwoLineTableCell
                    className="bolt-table-cell-content-with-inline-link no-v-padding"
                    columnIndex={columnIndex}
                    tableColumn={tableColumn}
                    key={"col-" + columnIndex}
                    iconProps={iconProps}
                    line1={topLine}
                    line2={bottomLine}
                />
            );
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div />
            </SimpleTableCell>
        );
    };

    private _getStageGraph = (tableItem: RunsListItemData, linkingService: IBuildLinkingService): JSX.Element | null => {
        if (tableItem.run && tableItem.stages && tableItem.stages.length > 0) {
            const stageNodeStructure: StageGraphNode[][][] = [];
            const stageHelper: StageGraphNode[][] = [];
            stageHelper.push(tableItem.stages.sort((a, b) => a.order - b.order).map(s => getStageNode(s, tableItem.run.id, tableItem.run.definition.name, this.context.pageContext, linkingService)));
            stageNodeStructure.push(stageHelper);

            return <StageGraph stages={stageNodeStructure} size={StatusSize.m} />;
        }
        return null;
    };

    private _renderStagesCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RunsListItemData>,
        tableItem: RunsListItemData
    ): JSX.Element => {
        const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const isStageOverflowEnabled = isFeatureFlagEnabled(this.context.pageContext, "Build2.RunsView.EnableStagesOverflowFunctionality");
        const canQueueBuild: boolean = this.props.security.canQueueBuild({ id: tableItem.run.definition.id, path:  tableItem.run.definition.path } as DefinitionReference);
        
        if(tableItem.run && tableItem.stages && tableItem.stages.length > 0 && isStageOverflowEnabled){ 
            return (
                <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                    <RunsTableStages 
                        key={"run-" + tableItem.run.id} 
                        pageContext={this.context.pageContext} 
                        canQueueBuild={canQueueBuild}
                        stages={tableItem.stages} 
                        buildId={tableItem.run.id} 
                        buildNumber={tableItem.run.buildNumber} 
                        pipelineName={tableItem.run.definition.name} 
                        appendCommitMessageToRunName={tableItem.run.appendCommitMessageToRunName} 
                        sourceVersionMessage={tableItem.sourceVersionMessage}
                    />
                </SimpleTableCell>
            );
        }

        const stageGraph = this._getStageGraph(tableItem, linkingService);
        if (stageGraph && !isStageOverflowEnabled) {
            return (
                <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                     {stageGraph}
                </SimpleTableCell>
            );
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div />
            </SimpleTableCell>
        );
    };

    private _renderStagesMobileCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RunsListItemData>,
        tableItem: RunsListItemData
    ): JSX.Element => {
        if (tableItem.run && !isFeatureFlagEnabled(this.context.pageContext, "Build2.RunsView.EnableOverviewStatus")) {
            const run = tableItem.run;
            const status = getBuildStatusIndicatorData(run.status, run.result);
            return (
                <TableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                    <div className="bolt-table-cell-content-nopadding stages-mobile">
                        <Tooltip text={status.label}>
                            <span className="flex-row">
                                <StatusComponent {...status.statusProps} size={StatusSize.m} />
                            </span>
                        </Tooltip>
                    </div>
                </TableCell>
            );
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div />
            </SimpleTableCell>
        );
    };

    private _renderBuildStatus = (tableItem: RunsListItemData): JSX.Element => {

        if (!tableItem.run) {
            return <></>;
        }

        const run = tableItem.run;
        const status = getBuildStatusIndicatorData(run.status, run.result);

        return (
            <Tooltip renderContent={() => this._renderToolTip(run.status, run.result, tableItem.stages, status.label)}>
                <div className="flex-row" aria-label={status.label}>
                    <StatusComponent {...status.statusProps}  className="icon-large-margin" size={StatusSize.m} />
                </div>
            </Tooltip>
        );        
    };

    private _renderToolTip(status: BuildStatus, result: BuildResult,  stages: FlatGraphRecord[], statusLabel: string): JSX.Element {   
        const tooltipStages = this._getStatusStagesTooltip(status, result, stages);
        if(tooltipStages.length > 4) {
            tooltipStages.splice(4, tooltipStages.length - 4, "...")
        }
        
        return (
            <>
                <div>{statusLabel}</div>
                { tooltipStages.map((name, key) => (<div key={key}>{name}</div>)) }
            </>
        );
    }

    private _getStatusStagesTooltip(status: BuildStatus, result: BuildResult, stages: FlatGraphRecord[]){      
        let tooltipStages: string[] = [];

        switch (status) {
            case BuildStatus.InProgress:
                tooltipStages = stages.filter(s => s.state == TimelineRecordState.InProgress ).map(s => s.name);
                break;
            case BuildStatus.NotStarted:
            case BuildStatus.Postponed:
                tooltipStages = stages.filter(s => s.state == TimelineRecordState.Pending).map(s => s.name);
                break;
            case BuildStatus.Completed:
                switch (result) {
                    case BuildResult.Failed:
                        tooltipStages = stages.filter(s => s.state == TimelineRecordState.Completed && s.result == TaskResult.Failed).map(s => s.name);
                        break;
                }
        }
        return tooltipStages;
    }

    private _renderTimesCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RunsListItemData>,
        tableItem: RunsListItemData
    ): JSX.Element => {
        if (tableItem.run) {
            return (
                <DateAndDuration
                    queueTime={tableItem.run.queueTime}
                    startTime={tableItem.run.startTime}
                    finishTime={tableItem.run.finishTime}
                    key={"col-" + columnIndex}
                    columnIndex={columnIndex}
                    tableColumn={tableColumn}
                />
            );
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div />
            </SimpleTableCell>
        );
    };

    private _showMenuForItem = (item: IRunRow) => {
        if (item?.run?.definition && this.props.security.isMember()) {
            const canDeleteRun = this.props.security.canDeleteBuild({
                id: item.run.definition.id,
                path: item.run.definition.path
            } as DefinitionReference);
            const canRetainRun = this.props.security.canRetainBuild({
                id: item.run.definition.id,
                path: item.run.definition.path
            } as DefinitionReference);
            let canCancelRun;
            if (this.props.userId) {
                canCancelRun = this.props.security.canCancelBuildByUserId(
                    this.props.userId,
                    item.run.definition.id.toString(),
                    item.run.definition.path,
                    item.run.requestedFor?.id,
                    item.run.requestedById
                );
            }
            return canDeleteRun || canRetainRun || !!canCancelRun;
        } else {
            return false;
        }
    };

    private _getMenuForItem = (item: IRunRow) => {
        const menuItems = [];

        const canDeleteRun = this.props.security.canDeleteBuild({
            id: item.run.definition.id,
            path: item.run.definition.path
        } as DefinitionReference);
        const canRetainRun = this.props.security.canRetainBuild({
            id: item.run.definition.id,
            path: item.run.definition.path
        } as DefinitionReference);
        if (this.props.userId) {
            const canCancelRun = this.props.security.canCancelBuildByUserId(
                this.props.userId,
                item.run.definition.id.toString(),
                item.run.definition.path,
                item.run.requestedFor?.id,
                item.run.requestedById
            );
            if (canCancelRun && item.run.status != BuildStatus.Completed) {
                menuItems.push({
                    text: Resources.Cancel,
                    id: "run-action-cancel",
                    onActivate: () => {
                        const confirmationText = format(Resources.ConfirmCancelRun, item.run.buildNumber);
                        this._confirmAction(Resources.CancelRun, confirmationText, () => {
                            this._store.cancelRun(item.run.id);
                        });
                    }
                });
            }
        }

        if (canRetainRun) {
            let runIsRetainedByUser = false;

            const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
            const vssPageData = vssPageService.getData();

            if (vssPageData && item.run.retentionLeases) {
                item.run.retentionLeases.forEach((retentionLease: RetentionLeaseData) => {
                    if (retentionLease.leaseOwnerIdentityRef && retentionLease.leaseOwnerIdentityRef.id === vssPageData.user.id) {
                        runIsRetainedByUser = true;
                    }
                });
            }

            if (runIsRetainedByUser) {
                menuItems.push({
                    text: Resources.StopRetainingRun,
                    id: "run-action-retain",
                    onActivate: () => {
                        this._store.stopRetainingRun(item.run.definition.id, item.run.id);
                    },
                    rank: 10
                });
            } else {
                menuItems.push({
                    text: Resources.RetainRun,
                    id: "run-action-retain",
                    onActivate: () => {
                        this._store.retainRun(item.run.definition.id, item.run.id);
                    },
                    rank: 10
                });
            }
        }

        if (item.run.retentionLeases && item.run.retentionLeases.length > 0) {
            const observableRetentionLeases = new ObservableArray<RetentionLeaseData>(item.run.retentionLeases);

            menuItems.push({
                text: Resources.ViewRetentionLeases,
                id: "run-action-view-retention",
                onActivate: () => {
                    const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                    layoutManager.renderCallout(onDismiss => {
                        const props: RetentionPanelProps = {
                            canDeleteLeases: canRetainRun,
                            onDelete: (ids: number[]) => {
                                this._store.deleteRetentionLeases(item.run.id, ids);

                                // Optimistically update observable to remove deleted item from displayed list
                                ids.forEach((leaseId: number) => {
                                    const indexToRemove = observableRetentionLeases.value.findIndex(
                                        (lease: RetentionLeaseData) => lease.id === leaseId
                                    );
                                    observableRetentionLeases.splice(indexToRemove, 1);
                                });
                            },
                            onDismiss: onDismiss,
                            retentionLeases: observableRetentionLeases
                        };

                        return React.createElement(RetentionPanel, props);
                    });
                },
                rank: 20
            });
        }

        if (canDeleteRun && !(item.run.retentionLeases && item.run.retentionLeases.length > 0)) {
            menuItems.push({
                text: Resources.Delete,
                id: "run-action-delete",
                onActivate: () => {
                    const confirmationText = format(Resources.ConfirmDeleteRun, item.run.buildNumber);
                    this._confirmAction(Resources.DeleteRun, confirmationText, () => {
                        this._store.deleteRun(item.run.id);
                    });
                },
                rank: 30
            });
        }

        const contributedItems = getMenuItems(this.context.pageContext, "ms.vss-build-web.completed-build-menu", item.run);
        menuItems.push(...contributedItems.value);

        return {
            id: "run-actions-menu",
            items: menuItems
        };
    };

    private _confirmAction = (title: string, description: string, callback: () => any) => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const props: IConfirmPipelineActionProps = {
                title: title,
                message: description,
                onClose: onDismiss,
                onConfirm: callback
            };

            return React.createElement(
                CommandComponent,
                {
                    commandType: "ciDConfirmPipelineComponent",
                    ...props
                },
                null
            );
        });
    };

    private _onSize = (event: MouseEvent, index: number, width: number, column: any) => {
        (column.width as ObservableValue<number>).value = width;
    };

    private _setColumns = () => {
        this._columns = [
            {
                id: "stages-mobile",
                width:  !isFeatureFlagEnabled(this.context.pageContext, "Build2.RunsView.EnableOverviewStatus") ? new ObservableValue(30) : 0,
                className: "pipelines-cell",
                renderCell: this._renderStagesMobileCell
            },
            {
                id: "description-mobile",
                width: new ObservableValue(300),
                name: Resources.DescriptionHeader,
                className: "pipelines-cell",
                renderCell: this._renderDescriptionMobileCell,
                columnStyle: TableColumnStyle.Primary
            },
            {
                id: "description",
                width: new ObservableValue(-60),
                name: Resources.DescriptionHeader,
                className: "pipelines-cell",
                onSize: this._onSize,
                renderCell: this._renderDescriptionNonMobileCell,
                columnStyle: TableColumnStyle.Primary
            },
            {
                id: "stages",
                width: new ObservableValue(-40),
                name: Resources.StagesHeader,
                className: "pipelines-cell",
                onSize: this._onSize,
                renderCell: this._renderStagesCell
            },
            {
                ariaLabel: Resources.TimeAndDuration,
                id: "time",
                width: new ObservableValue(156),
                className: "pipelines-cell",
                renderCell: this._renderTimesCell
            },
            new ColumnMore(this._getMenuForItem, this._showMenuForItem)
        ];
    };


    private _renderNewRunsRow = (index: number, item: IRunRow, details: ITableRowDetails<IRunRow>): JSX.Element => {
        const count = this._store.getNewRunsCount();
        const countString = count < MaxUpdates ? count.toString() : count.toString() + "+";
        const label = count > 1 ? format(Resources.NewRuns, countString) : Resources.NewRun;
        return (
            <TableRow index={index} details={details} className="runs-new-row">
                <SimpleTableCell className="no-v-padding" columnIndex={1} colspan={5}>
                    <div className="runs-new-row-content">{label}</div>
                </SimpleTableCell>
            </TableRow>
        );
    };

    private _renderLoadRow = (rowIndex: number, details: ITableRowDetails<IRunRow>): JSX.Element => {
        this._store.fetchMoreRuns();

        return (
            <TableRow className="bolt-list-row-loading" details={details} index={rowIndex}>
                {this._getTableColumns().map((column: ITableColumn<IRunRow>, columnIndex: number) => {
                    return TwoLineTableCell({
                        columnIndex,
                        line1: renderLoadingCell(column.columnLayout),
                        line2: renderLoadingCell(column.columnLayout)
                    });
                })}
            </TableRow>
        );
    };

    private _onActivateRow = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, row: ITableRow<IRunRow>) => {
        const pageContext = this.context.pageContext;

        if (row.data && row.data.isNewRunsRow) {
            this._store.fetchData(true);
        } else if (row.data && row.data.linkProps) {
            onClickFPS(pageContext, row.data.linkProps.href, true, event);
        }
    };

    private _onStoreChanged = () => {
        let runs: IRunRow[] = [];

        if (this._store.getNewRunsCount()) {
            runs.push({
                isNewRunsRow: true,
                renderRow: this._renderNewRunsRow
            } as IRunRow);
        }
        runs = runs.concat(this._store.getRunData().slice());

        this._itemProvider.splice(0, this._itemProvider.length, ...runs);
        if (this._store.hasMore()) {
            const asyncRow = new ObservableValue<IRunRow | undefined>(undefined);
            this._itemProvider.push(asyncRow);
        }

        const multiRepo = this._isMultiRepo(runs);
        this.setState({ multiRepo });

        const runsCount = runs.length;
        if (runsCount > 0) {
            Utils_Accessibility.announce(format(Resources.AnnounceFilterResults, runsCount));
        }
    };

    private _onZeroDataButtonClick = () => {
        const buildClient = this.context.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        const definitionId = this.props.definitionId;
        const tfsPageData = getTFSPageData(this.context.pageContext);
        const pageContext = this.context.pageContext;
        if (definitionId && !!tfsPageData && !!tfsPageData.project) {
            const projectId: string = tfsPageData.project.id;
            buildClient.getDefinition(projectId, definitionId).then((retrievedDefinition: BuildDefinition) => {
                const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout(onDismiss => {
                    const props: IQueueBuildProps = {
                        definition: retrievedDefinition,
                        repository: retrievedDefinition.repository,
                        processType: retrievedDefinition.process.type,
                        pageContext: pageContext,
                        project: retrievedDefinition.project.id,
                        source: "definitions",
                        onSuccess: (queuedBuild: Build) => {
                            // The new build should be added to table, but for now just using message bar to give user feedback
                            const globalMessageService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
                            globalMessageService.addBanner({
                                message: format(Resources.RunQueued, queuedBuild.buildNumber),
                                level: GlobalMessageLevel.success
                            });
                        },
                        definitionVariables: retrievedDefinition.variables,
                        queue: retrievedDefinition.queue,
                        defaultSourceBranch: retrievedDefinition.repository ? retrievedDefinition.repository.defaultBranch : undefined,
                        onDismiss
                    };

                    return React.createElement(
                        CommandComponent,
                        {
                            commandType: "ciQueueBuildComponent",
                            ...props
                        },
                        null
                    );
                });
            }, handleError);
        }
    };
    private _isMultiRepo(runs: IRunRow[]) {
        if (runs.length === 0) {
            return false;
        }

        const firstRepoId = runs[0]?.run?.triggeringRepository?.id || runs[0]?.run?.repository?.id;
        return (
            runs.filter(r => {
                if (!r.run) {
                    // Exclude ObservableValue intended to show "Loading row"
                    return false;
                }

                const repoId = r.run.triggeringRepository?.id || r.run.repository?.id;
                return repoId != firstRepoId;
            }).length > 0
        );
    }
    
}